import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Batch, Product, Site, Equipment, Ingredient, Location, InventoryItem, PackagingAction, BatchDetailsProps } from '../types/interfaces';
import { Status, ProductClass, BatchType, MaterialType, Account, Unit, Role } from '../types/enums'; // Added BatchType, MaterialType, Account, Unit
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import {
  checkKegForPackaging,
  isKegPackageType as isKegPackageTypeShared,
  postBatchPackaging,
  adjustVolumeAndPackage,
  productPackageChoices,
  choiceLabel,
  findBatchProduct,
} from '../utils/batchPackaging';
import UndoMoveModal from './facility/UndoMoveModal';
import KegScanOverlay from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';
import ChangeProductModal from './facility/ChangeProductModal';
import SplitBatchModal from './facility/SplitBatchModal';
import { canOperateFacility } from '../utils/facilityTransfer';
import { centralToday } from '../utils/centralDate';

interface BatchAction {
  id: number;
  action: string;
  timestamp: string;
  /** Imported packaging ledger events are labeled; not editable. */
  source?: 'batch_actions' | 'import' | string;
  readOnly?: boolean;
}

const BatchDetails: React.FC<BatchDetailsProps> = ({ inventory, refreshInventory }) => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = [Role.SuperAdmin, Role.Admin].includes(currentUser.role);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [items, setItems] = useState<{ name: string; type: string; enabled: number }[]>([]);
  const [actions, setActions] = useState<BatchAction[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(null);
  /** Optional bbl moved into the new vessel (e.g. FV→BT). Batch event only. */
  const [transferVolume, setTransferVolume] = useState('');
  const [newAction, setNewAction] = useState('');
  const [newBatchId, setNewBatchId] = useState('');
  const [newIngredient, setNewIngredient] = useState<Ingredient>({ itemName: '', quantity: 0, unit: 'lbs' });
  const [newIngredients, setNewIngredients] = useState<Ingredient[]>([{ itemName: '', quantity: 0, unit: 'lbs' }]);
  const [regressMode, setRegressMode] = useState(false);
  const [regressReason, setRegressReason] = useState('');
  const [showUndo, setShowUndo] = useState(false);
  const [showChangeProduct, setShowChangeProduct] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const reloadBatch = async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, { headers: { Accept: 'application/json' } });
      if (res.ok) setBatch(await res.json());
    } catch {
      /* keep current view */
    }
  };
  const canMove = (() => {
    try {
      return canOperateFacility(JSON.parse(localStorage.getItem('user') || '{}')?.role);
    } catch {
      return false;
    }
  })();
  const [stage, setStage] = useState<'' | 'Brewing' | 'Fermentation' | 'Aging' | 'Filtering/Carbonating' | 'Packaging' | 'Completed'>('');
  const [packageType, setPackageType] = useState<string>('');
  const [packageTypes, setPackageTypes] = useState<{ name: string; volume: number; enabled: number }[]>([]);
  const [productPackageTypes, setProductPackageTypes] = useState<{ type: string; itemName?: string | null }[]>([]);
  const [packageQuantity, setPackageQuantity] = useState<number>(0);
  const [packageLocation, setPackageLocation] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(new Set());
  const [showVolumePrompt, setShowVolumePrompt] = useState<{ message: string; shortfall: number } | null>(null);
  const [showTrueUpPrompt, setShowTrueUpPrompt] = useState<{
    estimatedVolume: number;
    packagedVolume: number;
    destroyedVolume: number;
    trubVolume: number;
    trueUpIncrease: number;
    trueUpDelta: number;
    unit?: string;
    message?: string;
    priorPeriodFlag?: boolean;
    eventDate?: string | null;
  } | null>(null);
  const [showReopenPrompt, setShowReopenPrompt] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [packagingActions, setPackagingActions] = useState<PackagingAction[]>([]);
  const [editPackaging, setEditPackaging] = useState<PackagingAction | null>(null);
  const [showKegPrompt, setShowKegPrompt] = useState<boolean>(false);
  const [kegCodes, setKegCodes] = useState<string[]>([]);
  const [currentKegCode, setCurrentKegCode] = useState('');
  const [showKegCamera, setShowKegCamera] = useState(false);
  // Latest list for camera scans (they can land before a re-render).
  const kegCodesRef = useRef<string[]>(kegCodes);
  kegCodesRef.current = kegCodes;
  const [manualKegEntry, setManualKegEntry] = useState(false);
  const [showProductionPrompt, setShowProductionPrompt] = useState<boolean>(false);
  const [producedVolume, setProducedVolume] = useState<number>(0);
  const [producedProof, setProducedProof] = useState<number>(0);
  const validStages = ['Brewing', 'Fermentation', 'Aging', 'Filtering/Carbonating', 'Packaging', 'Completed'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoints = [
          { url: `${API_BASE_URL}/api/batches/${batchId}`, setter: setBatch, name: 'batch', single: true },
          { url: `${API_BASE_URL}/api/products`, setter: setProducts, name: 'products' },
          { url: `${API_BASE_URL}/api/sites`, setter: setSites, name: 'sites' },
          { url: `${API_BASE_URL}/api/items`, setter: setItems, name: 'items' },
          { url: `${API_BASE_URL}/api/batches/${batchId}/actions`, setter: setActions, name: 'actions' },
          { url: `${API_BASE_URL}/api/batches/${batchId}/package`, setter: setPackagingActions, name: 'packaging' },
          { url: `${API_BASE_URL}/api/package-types`, setter: setPackageTypes, name: 'packageTypes' },
        ].filter(endpoint => endpoint.url !== null);

        const responses = await Promise.all(
          endpoints.map(({ url }) => apiFetch(url, { headers: { Accept: 'application/json' } }))
        );

        for (let i = 0; i < responses.length; i++) {
          const res = responses[i];
          const { name, setter, single } = endpoints[i];
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`Failed to fetch ${name}: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
          }
          const contentType = res.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const text = await res.text();
            throw new Error(`Invalid response for ${name}: Expected JSON, got ${contentType}`);
          }
          const data = await res.json();
          if (name === 'batch') {
            console.log(`[BatchDetails] Fetched batch ${batchId}:`, { ingredients: data.ingredients, recipeId: data.recipeId });
          }
          setter(single ? data : data);
        }
      } catch (err: any) {
        setError('Failed to load batch details: ' + err.message);
      }
    };
    fetchData();
  }, [batchId]);

  useEffect(() => {
    if (batch && products.length > 0) {
      const product: any = findBatchProduct(products as any[], batch as any);
      setProductPackageTypes(product?.packageTypes || []);
    }
  }, [batch, products]);

  useEffect(() => {
    if (!batch?.siteId) return;
    const fetchSiteData = async () => {
      try {
        const endpoints = [
          { url: `${API_BASE_URL}/api/equipment?siteId=${batch.siteId}`, setter: setEquipment, name: 'equipment' },
          { url: `${API_BASE_URL}/api/locations?siteId=${batch.siteId}`, setter: setLocations, name: 'locations' },
        ];
        const responses = await Promise.all(
          endpoints.map(({ url }) => apiFetch(url, { headers: { Accept: 'application/json' } }))
        );
        for (let i = 0; i < responses.length; i++) {
          const res = responses[i];
          const { name, setter } = endpoints[i];
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`Failed to fetch ${name}: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
          }
          const contentType = res.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const text = await res.text();
            throw new Error(`Invalid response for ${name}: Expected JSON, got ${contentType}`);
          }
          const data = await res.json();
          setter(data);
        }
      } catch (err: any) {
        setError('Failed to load site data: ' + err.message);
      }
    };
    fetchSiteData();
  }, [batch?.siteId]);

  const handleAddKegCode = async () => {
    const { code, error: kegError } = await checkKegForPackaging(currentKegCode, kegCodes);
    if (!code) {
      setError(kegError || 'Failed to add keg');
      return;
    }
    setKegCodes((k) => (k.includes(code) ? k : [...k, code]));
    setCurrentKegCode('');
    setError(null);
  };

  /** Camera scan: same keg-tracking checks as manual entry; stops at the packaged quantity. */
  // Full-screen scanner: throws to reject (buzz + red message in the scanner).
  const handleScannedKegCode = async (raw: string) => {
    const existing = kegCodesRef.current;
    const need = Number(packageQuantity) || 0;
    if (need > 0 && existing.length >= need) {
      throw new Error(`Already have ${need} keg(s) for this packaging`);
    }
    const { code, error: kegError } = await checkKegForPackaging(raw, existing);
    if (!code) throw new Error(kegError || 'Failed to add keg');
    kegCodesRef.current = existing.includes(code) ? existing : [...existing, code];
    setKegCodes((k) => (k.includes(code) ? k : [...k, code]));
    setError(null);
  };

  const isKegPackageType = isKegPackageTypeShared;

  const handlePackage = async () => {
    if (!packageType || packageQuantity <= 0 || !packageLocation) {
      setError('Please select a package type, quantity (> 0), and location');
      return;
    }
    if (isKegPackageType(packageType) && kegCodes.length !== packageQuantity) {
      setError(
        `Reconcile keg assets: scan or enter exactly ${packageQuantity} keg serials for ${packageType}`
      );
      setShowKegPrompt(true);
      return;
    }
    try {
      const data = await postBatchPackaging(batchId!, {
        packageType,
        quantity: packageQuantity,
        locationId: packageLocation || null,
        kegCodes: isKegPackageType(packageType) ? kegCodes : [],
      });
      if (data.prompt === 'volumeAdjustment') {
        setShowVolumePrompt({ message: data.message, shortfall: data.shortfall });
        return;
      }
      const batchRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        headers: { Accept: 'application/json' },
      });
      if (!batchRes.ok) {
        throw new Error(`Failed to refresh batch: HTTP ${batchRes.status}`);
      }
      const updatedBatch = await batchRes.json();
      setBatch(updatedBatch);
      const packagingRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package`, {
        headers: { Accept: 'application/json' },
      });
      if (packagingRes.ok) {
        setPackagingActions(await packagingRes.json());
      }
      await refreshInventory();
      setPackageType('');
      setPackageQuantity(0);
      setPackageLocation('');
      setKegCodes([]);
      setShowKegPrompt(false);
      setSuccessMessage(data.message || 'Packaged successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to package: ' + err.message);
    }
  };

  const handleVolumeAdjustment = async (confirm: boolean) => {
    if (!showVolumePrompt) return;
    if (!confirm) {
      setShowVolumePrompt(null);
      setError('Packaging cancelled due to insufficient volume');
      return;
    }
    try {
      const currentVolume = parseFloat(String(batch?.volume ?? 0));
      const shortfall = parseFloat(String(showVolumePrompt.shortfall));
      const data = await adjustVolumeAndPackage(batchId!, currentVolume, shortfall, {
        packageType,
        quantity: packageQuantity,
        locationId: packageLocation || null,
        kegCodes: packageType.includes('Keg') ? kegCodes : [],
      });
      const batchRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        headers: { Accept: 'application/json' },
      });
      if (!batchRes.ok) {
        throw new Error('Failed to refresh batch');
      }
      const updatedBatch = await batchRes.json();
      setBatch(updatedBatch);
      await refreshInventory();
      setPackageType('');
      setPackageQuantity(0);
      setPackageLocation('');
      setKegCodes([]);
      setShowVolumePrompt(null);
      setSuccessMessage(data.message || 'Packaged successfully after volume adjustment');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to adjust volume or package: ' + err.message);
    }
  };

  /** Shape a POST /actions response into a displayable row (fallback when the list reload fails). */
  const normalizeAddedAction = (raw: any, text: string, index: number): BatchAction => ({
    id: raw?.id ?? -(index + 1),
    action: raw?.action || text,
    timestamp: raw?.timestamp || new Date().toISOString(),
    source: raw?.source || 'batch_actions',
  } as BatchAction);

  const handleAddAction = async () => {
    if (!newAction) {
      setError('Action description is required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: newAction }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to add action: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const addedAction = await res.json().catch(() => ({}));
      // Reload the list so the new action shows with its saved id/timestamp
      // (never append a bare { message } response — that rendered a blank card).
      const listRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/actions`, {
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      const listed = listRes.ok ? await listRes.json().catch(() => null) : null;
      if (Array.isArray(listed)) {
        setActions(listed);
      } else {
        setActions((prev) => [...prev, normalizeAddedAction(addedAction, newAction, prev.length)]);
      }
      setNewAction('');
      setError(null);
      setSuccessMessage('Action added successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to add action: ' + err.message);
    }
  };

  const handleCompleteBatch = async () => {
    if (!batch) return;

    if (batch.productClass === ProductClass.Spirits && batch.batchType === BatchType.Fermentation) {
      setShowProductionPrompt(true);
      return;
    }

    try {
      // Server source of truth for remaining (vessel actual when available).
      const previewRes = await apiFetch(
        `${API_BASE_URL}/api/batches/${batchId}/complete-preview`,
        { headers: { Accept: 'application/json' } }
      );
      if (!previewRes.ok) {
        const text = await previewRes.text();
        throw new Error(
          `Failed to preview close remaining: HTTP ${previewRes.status}, Response: ${text.slice(0, 80)}`
        );
      }
      const preview = await previewRes.json();
      if (preview.shouldPrompt) {
        setShowTrueUpPrompt({
          estimatedVolume: Number(preview.estimatedVolume) || 0,
          packagedVolume: Number(preview.packagedVolume) || 0,
          destroyedVolume: Number(preview.destroyedVolume) || 0,
          trubVolume: Number(preview.trubVolume) || 0,
          trueUpIncrease: Number(preview.trueUpIncrease) || 0,
          trueUpDelta: Number(preview.trueUpDelta) || 0,
          unit: preview.unit || 'bbl',
          message: preview.message || '',
          priorPeriodFlag: !!preview.priorPeriodFlag,
          eventDate: preview.eventDate || null,
        });
        return;
      }

      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to complete batch: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const data = await res.json();
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              status: Status.Completed,
              stage: 'Completed',
              equipmentId: null,
              volume: data.volume ?? prev.volume,
            }
          : null
      );
      setError(null);
      setSuccessMessage('Batch completed successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      const actionsRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/actions`, {
        headers: { Accept: 'application/json' },
      });
      if (actionsRes.ok) setActions(await actionsRes.json());
    } catch (err: any) {
      setError('Failed to complete batch: ' + err.message);
    }
  };

  const handleProductionConfirmation = async () => {
    if (!producedVolume || !producedProof) {
      setError('Produced volume and proof are required');
      return;
    }
    try {
      const proofGallons = (producedVolume * producedProof) / 100;
      const defaultLocationId = locations[0]?.locationId;
      if (!batch?.siteId || !defaultLocationId) {
        setError('Site and at least one location are required to receive produced spirits');
        return;
      }
      const receiveData = {
        identifier: batchId,
        item: 'Distilled Spirits',
        type: 'Spirits',
        lotNumber: batchId,
        materialType: MaterialType.Spirits,
        quantity: producedVolume.toString(),
        unit: Unit.Gallons,
        proof: producedProof.toString(),
        source: 'Produced from fermentation',
        dspNumber: 'DSP-AL-20010',
        receivedDate: centralToday(),
        description: `Produced from batch ${batchId}`,
        cost: '0',
        poNumber: '',
        siteId: batch.siteId,
        locationId: defaultLocationId,
        status: 'Received',
        account: Account.Storage,
      };
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(receiveData),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to add produced spirits to inventory: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }

      const completeRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (!completeRes.ok) {
        const text = await completeRes.text();
        throw new Error(`Failed to complete batch: HTTP ${completeRes.status}, Response: ${text.slice(0, 50)}`);
      }
      setBatch((prev) => prev ? { ...prev, status: Status.Completed, stage: 'Completed', equipmentId: null } : null); 
      setShowProductionPrompt(false);
      await refreshInventory();
      setSuccessMessage('Produced spirits recorded and batch completed');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to record production or complete batch: ' + err.message);
    }
  };

  const handleTrueUpConfirmation = async (confirm: boolean) => {
    if (!showTrueUpPrompt || !batch) return;
    setShowTrueUpPrompt(null);
    if (!confirm) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to complete batch: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const data = await res.json();
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              status: Status.Completed,
              stage: 'Completed',
              equipmentId: null,
              volume: data.volume ?? prev.volume,
            }
          : null
      );
      const trub = Number(data.trubVolume) || 0;
      const up = Number(data.trueUpDelta) > 0 ? Number(data.trueUpDelta) : 0;
      setSuccessMessage(
        trub > 0
          ? `Batch completed; ${trub.toFixed(3)} bbl trub removed from production`
          : up > 0
            ? `Batch completed; production trued up by ${up.toFixed(3)} bbl`
            : 'Batch completed successfully'
      );
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
      const actionsRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/actions`, {
        headers: { Accept: 'application/json' },
      });
      if (actionsRes.ok) setActions(await actionsRes.json());
    } catch (err: any) {
      setError('Failed to complete batch: ' + err.message);
    }
  };

  const handleReopenBatch = async () => {
    if (!batch || !reopenReason.trim()) {
      setError('A reason is required to reopen a batch');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ reason: reopenReason.trim() }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to reopen batch: HTTP ${res.status}, Response: ${text.slice(0, 80)}`);
      }
      const data = await res.json();
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              status: 'In Progress' as Status,
              stage: (data.stage as any) || 'Packaging',
              volume: data.volume,
            }
          : null
      );
      setShowReopenPrompt(false);
      setReopenReason('');
      setSuccessMessage(
        `Batch reopened; restored ${Number(data.volumeRestored || 0).toFixed(3)} bbl`
      );
      setTimeout(() => setSuccessMessage(null), 2500);
      setError(null);
      const [actionsRes, pkgRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/batches/${batchId}/actions`, {
          headers: { Accept: 'application/json' },
        }),
        apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package`, {
          headers: { Accept: 'application/json' },
        }),
      ]);
      if (actionsRes.ok) setActions(await actionsRes.json());
      if (pkgRes.ok) setPackagingActions(await pkgRes.json());
    } catch (err: any) {
      setError('Failed to reopen batch: ' + err.message);
    }
  };

  const handleEditBatchName = async () => {
    if (!newBatchId) {
      setError('New batch ID is required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ batchId: newBatchId }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update batch ID: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      setBatch((prev) => prev ? { ...prev, batchId: newBatchId } : null);
      setNewBatchId('');
      setError(null);
      setSuccessMessage('Batch ID updated successfully');
      setTimeout(() => {
        setSuccessMessage(null);
        navigate(`/production/${newBatchId}`);
      }, 2000);
    } catch (err: any) {
      setError('Failed to update batch ID: ' + err.message);
    }
  };

  const handleDeleteBatch = async () => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to delete batch: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      setError(null);
      setSuccessMessage('Batch deleted successfully');
      setTimeout(() => {
        setSuccessMessage(null);
        navigate('/production');
      }, 2000);
    } catch (err: any) {
      setError('Failed to delete batch: ' + err.message);
    }
  };

  const handleAddIngredient = async () => {
    if (!newIngredient.itemName || newIngredient.quantity <= 0 || !newIngredient.unit) {
      setError('Valid item, quantity, and unit are required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/ingredients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(newIngredient),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to add ingredient: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const updatedBatch = await res.json();
      setBatch(updatedBatch);
      setNewIngredient({ itemName: '', quantity: 0, unit: 'lbs' });
      setError(null);
      setSuccessMessage('Ingredient added successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to add ingredient: ' + err.message);
    }
  };

  const handleUpdateIngredient = async (original: Ingredient, updated: Ingredient) => {
    if (!original.itemName || !original.quantity || !original.unit ||
        !updated.itemName || updated.quantity <= 0 || !updated.unit) {
      setError('Valid original and updated item, quantity, and unit are required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/ingredients`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ original, updated }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update ingredient: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const updatedBatch = await res.json();
      setBatch(updatedBatch);
      setNewIngredients(newIngredients.map(ing => 
        ing.itemName === original.itemName && ing.quantity === original.quantity && ing.unit === original.unit ? updated : ing
      ));
      setError(null);
      setSuccessMessage('Ingredient updated successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to update ingredient: ' + err.message);
    }
  };

  const handleDeleteIngredient = async (ingredient: Ingredient) => {
    if (!ingredient.itemName || !ingredient.quantity || !ingredient.unit) {
      setError('Valid item, quantity, and unit are required for deletion');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/ingredients`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(ingredient),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to delete ingredient: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const updatedBatch = await res.json();
      setBatch(updatedBatch);
      setNewIngredients(newIngredients.filter(ing => 
        !(ing.itemName === ingredient.itemName && ing.quantity === ingredient.quantity && ing.unit === ingredient.unit)
      ));
      setError(null);
      setSuccessMessage('Ingredient deleted successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to delete ingredient: ' + err.message);
    }
  };

  const handleStageSelect = (nextStage: '' | 'Brewing' | 'Fermentation' | 'Aging' | 'Filtering/Carbonating' | 'Packaging' | 'Completed') => {
    setStage(nextStage);
    // When moving to Fermentation, default equipment to the fermenter chosen at creation.
    if (nextStage === 'Fermentation' && batch?.fermenterId && !selectedEquipmentId) {
      setSelectedEquipmentId(batch.fermenterId);
    }
  };

  const handleUpdateEquipment = async () => {
    if (!stage) {
      setError('Stage is required');
      return;
    }
    const terminal = stage === 'Packaging' || stage === 'Completed';
    const equipmentId =
      selectedEquipmentId ||
      (stage === 'Fermentation' && batch?.fermenterId ? batch.fermenterId : null);
    if (!terminal && !equipmentId) {
      setError('Equipment and stage are required');
      return;
    }
    if (regressMode) {
      if (!regressReason.trim()) {
        setError('Enter a reason to move the batch back');
        return;
      }
      if (!window.confirm(`Move ${batchId} back to ${stage}? Reason: ${regressReason.trim()}`)) return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          equipmentId: equipmentId || null,
          stage,
          ...(transferVolume.trim() ? { transferVolume: Number(transferVolume) } : {}),
          ...(regressMode ? { regress: true, reason: regressReason.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Failed to update equipment: HTTP ${res.status}, Response: ${text.slice(0, 80)}`;
        try {
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch {
          /* keep message */
        }
        throw new Error(message);
      }
      const data = await res.json();
      setBatch((prev) => prev ? { ...prev, equipmentId: data.equipmentId, stage: data.stage } : null);
      setSelectedEquipmentId(data.equipmentId || null);
      setStage('');
      setError(null);
      setTransferVolume('');
      setRegressMode(false);
      setRegressReason('');
      setSuccessMessage('Equipment and stage updated successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError('Failed to update equipment: ' + err.message);
    }
  };

  const handleEditPackaging = async () => {
    if (editPackaging?.readOnly) {
      setError('Imported packaging history is read-only');
      return;
    }
    if (!editPackaging || editPackaging.quantity < 0) {
      setError('Valid quantity is required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package/${editPackaging.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ quantity: editPackaging.quantity }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update packaging: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const data = await res.json();
      const batchRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        headers: { Accept: 'application/json' },
      });
      if (!batchRes.ok) {
        throw new Error('Failed to refresh batch');
      }
      const updatedBatch = await batchRes.json();
      setBatch(updatedBatch);
      const packagingRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package`, {
        headers: { Accept: 'application/json' },
      });
      if (packagingRes.ok) {
        setPackagingActions(await packagingRes.json());
      }
      await refreshInventory();
      setEditPackaging(null);
      setSuccessMessage('Packaging updated successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to update packaging: ' + err.message);
    }
  };

  const handleDeletePackaging = async (pkg: PackagingAction) => {
    if (pkg.readOnly) {
      setError('Imported packaging history is read-only');
      return;
    }
    if (pendingDeletions.has(pkg.id.toString())) return;
    setPendingDeletions(prev => new Set(prev).add(pkg.id.toString()));
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package/${pkg.id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to delete packaging: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const batchRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}`, {
        headers: { Accept: 'application/json' },
      });
      if (!batchRes.ok) {
        throw new Error('Failed to refresh batch');
      }
      const updatedBatch = await batchRes.json();
      setBatch(updatedBatch);
      const packagingRes = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package`, {
        headers: { Accept: 'application/json' },
      });
      if (packagingRes.ok) {
        setPackagingActions(await packagingRes.json());
      }
      await refreshInventory();
      setSuccessMessage('Packaging deleted successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
    } catch (err: any) {
      setError('Failed to delete packaging: ' + err.message);
    } finally {
      setPendingDeletions(prev => {
        const newSet = new Set(prev);
        newSet.delete(pkg.id.toString());
        return newSet;
      });
    }
  };

  const handlePrintBatchSheet = async () => {
    if (!batch) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    doc.setFontSize(18);
    doc.text('Batch Sheet', margin, 20);
    doc.setFontSize(12);
    doc.text(`Batch ID: ${batch.batchId}`, margin, 30);
    doc.text(`Product: ${batch.productName || 'N/A'}`, margin, 40);
    doc.text(`Site: ${batch.siteName || 'N/A'}`, margin, 50);
    doc.text(`Status: ${batch.status}`, margin, 60);
    doc.text(`Date: ${batch.date}`, margin, 70);
    doc.text(`Volume: ${batch.volume ? batch.volume.toFixed(3) : 'N/A'} barrels`, margin, 80);
    doc.text(`Stage: ${batch.stage || 'N/A'}`, margin, 90);

    let yPos = 100;
    if (batch.ingredients && batch.ingredients.length > 0) {
      doc.text('Ingredients:', margin, yPos);
      yPos += 10;
      autoTable(doc, {
        startY: yPos,
        head: [['Item', 'Quantity', 'Unit', 'Source']],
        body: batch.ingredients.map(ing => [
          ing.itemName,
          ing.quantity.toString(),
          ing.unit,
          ing.isRecipe ? 'Recipe' : 'Additional'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        margin: { left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.4 },
          1: { cellWidth: contentWidth * 0.2 },
          2: { cellWidth: contentWidth * 0.2 },
          3: { cellWidth: contentWidth * 0.2 },
        },
        styles: { fontSize: 8 },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    if (actions.length > 0) {
      doc.text('Actions:', margin, yPos);
      yPos += 10;
      autoTable(doc, {
        startY: yPos,
        head: [['Timestamp', 'Action']],
        body: actions.map(action => [action.timestamp, action.action]),
        theme: 'striped',
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        margin: { left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.3 },
          1: { cellWidth: contentWidth * 0.7 },
        },
        styles: { fontSize: 8 },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    if (packagingActions.length > 0) {
      doc.text('Packaging Actions:', margin, yPos);
      yPos += 10;
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Package Type', 'Quantity', 'Volume (barrels)', 'Location', 'Keg Codes']],
        body: packagingActions.map(pkg => [
          pkg.date,
          pkg.packageType,
          pkg.quantity.toString(),
          Number(pkg.volume || 0).toFixed(3),
          locations.find(loc => loc.locationId === pkg.locationId)?.name ||
            pkg.locationName ||
            pkg.locationId ||
            'N/A',
          Array.isArray(pkg.kegCodes)
            ? pkg.kegCodes.join(', ')
            : pkg.keg_codes
              ? (() => {
                  try {
                    return JSON.parse(pkg.keg_codes!).join(', ');
                  } catch {
                    return 'N/A';
                  }
                })()
              : 'N/A',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        margin: { left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.15 },
          1: { cellWidth: contentWidth * 0.15 },
          2: { cellWidth: contentWidth * 0.15 },
          3: { cellWidth: contentWidth * 0.15 },
          4: { cellWidth: contentWidth * 0.2 },
          5: { cellWidth: contentWidth * 0.2 },
        },
        styles: { fontSize: 8 },
      });
    }

    doc.save(`Batch_${batch.batchId}_Sheet.pdf`);
  };

  if (!batch) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-container container">
      <h2 className="text-warning mb-4">Batch {batch.batchId}</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}
      <div className="row mb-4">
        <div className="col-md-6">
          <h3 className="text-warning mb-3">Batch Details</h3>
          <p><strong>Product:</strong> {batch.productName || 'N/A'}</p>
          <p><strong>Site:</strong> {batch.siteName || 'N/A'}</p>
          <p><strong>Status:</strong> {batch.status}</p>
          <p><strong>Date:</strong> {batch.date}</p>
          {(() => {
            const fmtBbl = (v: number | null | undefined, wholePrefer = false) => {
              if (v == null || !Number.isFinite(Number(v)) || Number(v) < 0) return null;
              const n = Number(v);
              if (wholePrefer && (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-9)) {
                return `${Math.round(n)} BBL`;
              }
              return `${n.toFixed(3)} BBL`;
            };
            const brewed = batch.brewedVolume != null ? batch.brewedVolume : batch.volume;
            const produced = batch.producedVolume;
            const packaged = batch.packagedVolume;
            const remaining = batch.remainingVolume;
            const lost = batch.lostVolume;
            const trub = batch.trubVolume;
            return (
              <>
                <p>
                  <strong>Brewed:</strong>{' '}
                  {fmtBbl(brewed, true) || 'N/A'}
                  {batch.volumeSource === 'ekos' || batch.volumeSource === 'stored'
                    ? ' (from Ekos)'
                    : ''}
                </p>
                <p>
                  <strong>Produced:</strong>{' '}
                  {fmtBbl(produced) || '—'}
                </p>
                {trub != null && Number(trub) > 0 && (
                  <p>
                    <strong>Trub:</strong>{' '}
                    {fmtBbl(trub)}
                    {' (production true-up; removed from Produced)'}
                  </p>
                )}
                <p>
                  <strong>Packaged:</strong>{' '}
                  {fmtBbl(packaged) || '—'}
                </p>
                <p>
                  <strong>Remaining:</strong>{' '}
                  {fmtBbl(remaining) || '—'}
                  {remaining != null && batch.remainingBasis === 'actual' && batch.actualVolume != null
                    ? ` (from actual ${Number(batch.actualVolume).toFixed(3)} BBL ${
                        batch.actualVolumeSource === 'brite_entry' ? 'brite entry' : 'transfer'
                      }${batch.actualVolumeDate ? ` ${batch.actualVolumeDate}` : ''})`
                    : ''}
                  {remaining != null
                    ? ' (still on hand / WIP — not recorded as loss)'
                    : ''}
                </p>
                <p>
                  <strong>Lost:</strong>{' '}
                  {fmtBbl(lost) || '—'}
                  {lost == null
                    ? ' (not recorded; Brewed−Packaged is not assumed loss)'
                    : ''}
                </p>
                {batch.packagedOverBrew != null && Number(batch.packagedOverBrew) > 0 && (
                  <p>
                    <strong>Packaged over brew:</strong>{' '}
                    {fmtBbl(batch.packagedOverBrew)}
                  </p>
                )}
              </>
            );
          })()}
          <p><strong>Stage:</strong> {batch.stage || 'N/A'}</p>
          {batch.stage === 'Aging' && (
            <p className="text-muted" data-testid="aging-blend-hint">
              Blend into a brite tank before packaging.
            </p>
          )}
          <p><strong>Equipment:</strong> {equipment.find(e => e.equipmentId === batch.equipmentId)?.name || 'N/A'}</p>
          {batch.status !== Status.Completed && ( 
            <div className="mt-3">
              <h4 className="text-warning mb-2">Update Batch ID</h4>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="text"
                  value={newBatchId}
                  onChange={(e) => setNewBatchId(e.target.value)}
                  placeholder="New Batch ID"
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                />
                <button className="btn btn-primary" onClick={handleEditBatchName}>
                  Update ID
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="col-md-6">
          <h3 className="text-warning mb-3">Actions</h3>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <button className="btn btn-primary" onClick={handlePrintBatchSheet}>
              Print Batch Sheet
            </button>
            {batch.status !== Status.Completed && (
              <>
                <button className="btn btn-success" onClick={handleCompleteBatch}>
                  Complete Batch
                </button>
                <button className="btn btn-danger" onClick={handleDeleteBatch}>
                  Delete Batch
                </button>
              </>
            )}
            {batch.status !== Status.Completed && canMove && (
              <>
                <button className="btn btn-outline-warning" type="button" onClick={() => setShowChangeProduct(true)} data-testid="batch-change-product">
                  Change product
                </button>
                <button className="btn btn-outline-warning" type="button" onClick={() => setShowSplit(true)} data-testid="batch-split">
                  ⑂ Split batch
                </button>
              </>
            )}
            {showChangeProduct && (
              <ChangeProductModal
                batch={batch as any}
                onCancel={() => setShowChangeProduct(false)}
                onDone={async (message) => {
                  setShowChangeProduct(false);
                  setPackageType('');
                  await reloadBatch();
                  setSuccessMessage(message);
                  setTimeout(() => setSuccessMessage(null), 4000);
                }}
              />
            )}
            {showSplit && (
              <SplitBatchModal
                batchId={batchId || ''}
                onCancel={() => setShowSplit(false)}
                onDone={async (message) => {
                  setShowSplit(false);
                  await reloadBatch();
                  setSuccessMessage(message);
                  setTimeout(() => setSuccessMessage(null), 5000);
                }}
              />
            )}
            {batch.status === Status.Completed && isAdmin && (
              <button
                className="btn btn-warning"
                onClick={() => {
                  setReopenReason('');
                  setShowReopenPrompt(true);
                }}
              >
                Reopen Batch
              </button>
            )}
          </div>
          {batch.status !== Status.Completed && (
            <div className="mb-3">
              <h4 className="text-warning mb-2">Update Equipment & Stage</h4>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <select
                  value={selectedEquipmentId || ''}
                  onChange={(e) => setSelectedEquipmentId(parseInt(e.target.value) || null)}
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                  data-testid="batch-equipment-select"
                >
                  <option value="">Select Equipment</option>
                  {equipment.map(eq => (
                    <option key={eq.equipmentId} value={eq.equipmentId}>
                      {eq.name}
                      {batch?.fermenterId === eq.equipmentId ? ' (fermenter)' : ''}
                    </option>
                  ))}
                </select>
                <select
                  value={stage}
                  onChange={(e) => handleStageSelect(e.target.value as any)}
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                  data-testid="batch-stage-select"
                >
                  <option value="">Select Stage</option>
                  {validStages.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={transferVolume}
                  onChange={(e) => setTransferVolume(e.target.value)}
                  placeholder="Volume transferred (bbl, optional)"
                  title="Bbl actually moved into the new vessel, e.g. 28 of a 30 bbl batch. Used for Facility View 'left' and FV→BT Wort→Brite WIP inventory convert (no TTB)."
                  className="form-control"
                  style={{ maxWidth: '240px' }}
                  data-testid="batch-transfer-volume"
                />
                <button className="btn btn-primary" onClick={handleUpdateEquipment} data-testid="batch-equipment-update">
                  {regressMode ? 'Move back' : 'Update'}
                </button>
                {canMove && <button
                  className="btn btn-outline-warning"
                  type="button"
                  onClick={() => setShowUndo(true)}
                  data-testid="batch-undo-move"
                  title="Revert the most recent vessel/stage move"
                >
                  ↶ Undo last move
                </button>}
              </div>
              {canMove && <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                <label className="text-warning mb-0" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={regressMode}
                    onChange={(e) => setRegressMode(e.target.checked)}
                    data-testid="batch-regress-toggle"
                  />
                  Move back / regress stage
                </label>
                {regressMode && (
                  <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: '360px' }}
                    placeholder="Reason (required)"
                    value={regressReason}
                    onChange={(e) => setRegressReason(e.target.value)}
                    data-testid="batch-regress-reason"
                  />
                )}
              </div>}
              {showUndo && (
                <UndoMoveModal
                  batchId={batchId || ''}
                  equipment={equipment as any}
                  onCancel={() => setShowUndo(false)}
                  onDone={(message, r) => {
                    setShowUndo(false);
                    setBatch((prev) => (prev ? { ...prev, equipmentId: r.equipmentId as any, stage: r.stage as any } : prev));
                    setSelectedEquipmentId((r.equipmentId as any) || null);
                    setSuccessMessage(message);
                    setTimeout(() => setSuccessMessage(null), 3000);
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {batch.status !== Status.Completed && (
        <div className="mb-4">
          <h3 className="text-warning mb-3">Add Action</h3>
          <div className="batch-add-action" data-testid="batch-add-action">
            <input
              type="text"
              value={newAction}
              onChange={(e) => setNewAction(e.target.value)}
              placeholder="Enter action description"
              className="form-control"
              data-testid="batch-add-action-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddAction();
                }
              }}
            />
            <button className="btn btn-primary" data-testid="batch-add-action-submit" onClick={handleAddAction}>
              Add Action
            </button>
          </div>
        </div>
      )}
      <div className="mb-4">
        <h3 className="text-warning mb-3">Actions</h3>
        {actions.length > 0 ? (
          <>
            <table className="inventory-table table table-striped">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {actions.map(action => (
                  <tr key={action.id}>
                    <td>{action.timestamp}</td>
                    <td>{action.action}</td>
                    <td>{action.source === 'import' ? 'Ekos import' : 'Recorded'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="action-list">
              {actions.map(action => (
                <div key={action.id} className="action-card card mb-2">
                  <div className="card-body">
                    <p className="card-text"><strong>Timestamp:</strong> {action.timestamp}</p>
                    <p className="card-text"><strong>Action:</strong> {action.action}</p>
                    <p className="card-text">
                      <strong>Source:</strong>{' '}
                      {action.source === 'import' ? 'Ekos import' : 'Recorded'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>
            No batch actions recorded. Production actions from Ekos (tank moves, volume
            adjustments, etc.) are not imported yet — a follow-up. Packaging lines stay in
            Packaging below.
          </p>
        )}
      </div>
      <div className="mb-4">
        <h3 className="text-warning mb-3">Ingredients</h3>
        {batch.ingredients && batch.ingredients.length > 0 ? (
          <>
            <table className="inventory-table table table-striped">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Source</th>
                  {batch.status !== Status.Completed && <th>Actions</th>} 
                </tr>
              </thead>
              <tbody>
                {batch.ingredients.map((ing, index) => (
                  <tr key={index}>
                    <td>
                      {newIngredients.some(n => 
                        n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                      ) ? (
                        <select
                          value={newIngredients.find(n => 
                            n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                          )?.itemName || ing.itemName}
                          onChange={(e) => {
                            const updated = { ...ing, itemName: e.target.value };
                            setNewIngredients(prev => prev.map(n => 
                              n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit ? updated : n
                            ));
                          }}
                          className="form-control"
                        >
                          <option value="">Select Item</option>
                          {items.filter(item => item.enabled).map(item => (
                            <option key={item.name} value={item.name}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        ing.itemName
                      )}
                    </td>
                    <td>
                      {newIngredients.some(n => 
                        n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                      ) ? (
                        <input
                          type="number"
                          value={newIngredients.find(n => 
                            n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                          )?.quantity || ing.quantity}
                          onChange={(e) => {
                            const updated = { ...ing, quantity: parseFloat(e.target.value) || 0 };
                            setNewIngredients(prev => prev.map(n => 
                              n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit ? updated : n
                            ));
                          }}
                          className="form-control"
                          style={{ width: '80px' }}
                        />
                      ) : (
                        ing.quantity
                      )}
                    </td>
                    <td>
                      {newIngredients.some(n => 
                        n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                      ) ? (
                        <select
                          value={newIngredients.find(n => 
                            n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                          )?.unit || ing.unit}
                          onChange={(e) => {
                            const updated = { ...ing, unit: e.target.value };
                            setNewIngredients(prev => prev.map(n => 
                              n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit ? updated : n
                            ));
                          }}
                          className="form-control"
                        >
                          <option value="lbs">lbs</option>
                          <option value="gallons">gallons</option>
                          <option value="liters">liters</option>
                        </select>
                      ) : (
                        ing.unit
                      )}
                    </td>
                    <td>{ing.isRecipe ? 'Recipe' : 'Additional'}</td>
                    {batch.status !== Status.Completed && (
                      <td>
                        {newIngredients.some(n => 
                          n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                        ) ? (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleUpdateIngredient(ing, newIngredients.find(n => 
                                n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit
                              )!)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => setNewIngredients(newIngredients.filter(n => 
                                !(n.itemName === ing.itemName && n.quantity === ing.quantity && n.unit === ing.unit)
                              ))}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setNewIngredients([...newIngredients, { ...ing }])}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteIngredient(ing)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ingredient-list">
              {batch.ingredients.map((ing, index) => (
                <div key={index} className="ingredient-card card mb-2">
                  <div className="card-body">
                    <p className="card-text"><strong>Item:</strong> {ing.itemName}</p>
                    <p className="card-text"><strong>Quantity:</strong> {ing.quantity}</p>
                    <p className="card-text"><strong>Unit:</strong> {ing.unit}</p>
                    <p className="card-text"><strong>Source:</strong> {ing.isRecipe ? 'Recipe' : 'Additional'}</p>
                    {batch.status !== Status.Completed && (
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setNewIngredients([...newIngredients, { ...ing }])}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteIngredient(ing)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>No ingredients recorded.</p>
        )}
      </div>
      {batch.status !== Status.Completed && (
        <div className="mb-4">
          <h3 className="text-warning mb-3">Add Ingredients</h3>
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <label className="form-label text-warning">Item:</label>
              <select
                value={newIngredient.itemName}
                onChange={(e) => setNewIngredient({ ...newIngredient, itemName: e.target.value })}
                className="form-control"
              >
                <option value="">Select Item</option>
                {items.filter(item => item.enabled).map(item => (
                  <option key={item.name} value={item.name}>{item.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label text-warning">Quantity:</label>
              <input
                type="number"
                value={newIngredient.quantity || ''}
                onChange={(e) => setNewIngredient({ ...newIngredient, quantity: parseFloat(e.target.value) || 0 })}
                placeholder="Quantity"
                min="0"
                className="form-control"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-warning">Unit:</label>
              <select
                value={newIngredient.unit}
                onChange={(e) => setNewIngredient({ ...newIngredient, unit: e.target.value })}
                className="form-control"
              >
                <option value="lbs">lbs</option>
                <option value="gallons">gallons</option>
                <option value="liters">liters</option>
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={handleAddIngredient}>
                Add Ingredient
              </button>
            </div>
          </div>
        </div>
      )}
      {batch.stage === 'Packaging' && batch.status !== Status.Completed && (
        <div className="mb-4">
          <h3 className="text-warning mb-3">Package Batch</h3>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <label className="form-label text-warning">Package Type:</label>
              <select
                value={packageType}
                onChange={(e) => setPackageType(e.target.value)}
                className="form-control"
              >
                <option value="">Select Package Type</option>
                {productPackageChoices(productPackageTypes, packageTypes).map((c) => (
                  <option key={c.type} value={c.type}>{choiceLabel(c)}</option>
                ))}
              </select>
              {products.length > 0 && productPackageChoices(productPackageTypes, packageTypes).length === 0 && (
                <small className="text-warning d-block" data-testid="batch-no-package-types">
                  {batch?.productName || 'This product'} has no package types set up.{' '}
                  <a href={batch?.productId != null ? `/products/${batch.productId}` : '/products'}>Add them in product setup</a>.
                </small>
              )}
            </div>
            <div className="col-md-4">
              <label className="form-label text-warning">Quantity:</label>
              <input
                type="number"
                value={packageQuantity || ''}
                onChange={(e) => setPackageQuantity(parseInt(e.target.value) || 0)}
                placeholder="Quantity"
                min="0"
                className="form-control"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-warning">Location:</label>
              <select
                value={packageLocation}
                onChange={(e) => setPackageLocation(e.target.value)}
                className="form-control"
              >
                <option value="">Select Location</option>
                {locations.map(loc => (
                  <option key={loc.locationId} value={loc.locationId}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
          {packageType.includes('Keg') && (
            <div className="mt-3">
              <label className="form-label text-warning">Keg Codes:</label>
              <div className="d-flex gap-2 align-items-center mb-2">
                <input
                  type="text"
                  value={currentKegCode}
                  onChange={(e) => setCurrentKegCode(e.target.value)}
                  placeholder="Scan or enter keg code"
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                />
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    checked={manualKegEntry}
                    onChange={(e) => setManualKegEntry(e.target.checked)}
                    className="form-check-input"
                  />
                  Manual Entry
                </label>
                <button className="btn btn-primary" onClick={handleAddKegCode}>
                  Add Keg
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-testid="batch-keg-camera"
                  aria-pressed={showKegCamera}
                  onClick={() => {
                    primeScanAudio(); // iOS: sound needs this tap
                    setShowKegCamera(true);
                  }}
                >
                  Scan with camera
                </button>
              </div>
              {showKegCamera && (
                <KegScanOverlay
                  title={`Package ${batch?.batchId ?? ''}`}
                  subtitle={`${batch?.productName || 'Product'} · ${packageType}`}
                  codes={kegCodes}
                  target={Number(packageQuantity) > 0 ? Number(packageQuantity) : undefined}
                  onScan={handleScannedKegCode}
                  onRemove={(c) => setKegCodes((k) => k.filter((x) => x !== c))}
                  onDone={() => setShowKegCamera(false)}
                />
              )}
              <p className="mb-1">
                Kegs: {kegCodes.length} of {Number(packageQuantity) || 0}
              </p>
              {kegCodes.length > 0 && (
                <div>
                  <p><strong>Added Kegs:</strong> {kegCodes.join(', ')}</p>
                  <button className="btn btn-danger btn-sm" onClick={() => setKegCodes([])}>
                    Clear Kegs
                  </button>
                </div>
              )}
            </div>
          )}
          <button className="btn btn-primary mt-3" onClick={() => handlePackage()}>
            Package
          </button>
        </div>
      )}
      <div className="mb-4">
        <h3 className="text-warning mb-3">Packaging Actions</h3>
        {packagingActions.length > 0 ? (
          <>
            <table className="inventory-table table table-striped">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Package Type</th>
                  <th>Quantity</th>
                  <th>Volume (barrels)</th>
                  <th>Location</th>
                  <th>Keg Codes</th>
                  <th>Source</th>
                  {batch.status !== Status.Completed && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {packagingActions.map(pkg => {
                  const kegLabel = Array.isArray(pkg.kegCodes)
                    ? pkg.kegCodes.join(', ')
                    : pkg.keg_codes
                      ? (() => {
                          try {
                            return JSON.parse(pkg.keg_codes!).join(', ');
                          } catch {
                            return 'N/A';
                          }
                        })()
                      : 'N/A';
                  const locLabel =
                    locations.find(loc => loc.locationId === pkg.locationId)?.name ||
                    pkg.locationName ||
                    pkg.locationId ||
                    'N/A';
                  const canEdit = !pkg.readOnly && batch.status !== Status.Completed;
                  return (
                  <tr key={pkg.id}>
                    <td>{pkg.date}</td>
                    <td>{pkg.packageType}</td>
                    <td>
                      {editPackaging && editPackaging.id === pkg.id ? (
                        <input
                          type="number"
                          value={editPackaging.quantity}
                          onChange={(e) => setEditPackaging({ ...editPackaging, quantity: parseInt(e.target.value) || 0 })}
                          className="form-control"
                          style={{ width: '80px' }}
                        />
                      ) : (
                        pkg.quantity
                      )}
                    </td>
                    <td>{Number(pkg.volume || 0).toFixed(3)}</td>
                    <td>{locLabel}</td>
                    <td>{kegLabel}</td>
                    <td>{pkg.source === 'import' ? 'Ekos import' : 'Recorded'}</td>
                    {batch.status !== Status.Completed && (
                      <td>
                        {!canEdit ? (
                          <span className="text-muted">Read-only</span>
                        ) : editPackaging && editPackaging.id === pkg.id ? (
                          <div className="d-flex gap-2">
                            <button className="btn btn-primary btn-sm" onClick={handleEditPackaging}>
                              Save
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => setEditPackaging(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2">
                            <button className="btn btn-primary btn-sm" onClick={() => setEditPackaging(pkg)}>
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeletePackaging(pkg)}
                              disabled={pendingDeletions.has(pkg.id.toString())}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="packaging-list">
              {packagingActions.map(pkg => {
                const kegLabel = Array.isArray(pkg.kegCodes)
                  ? pkg.kegCodes.join(', ')
                  : pkg.keg_codes
                    ? (() => {
                        try {
                          return JSON.parse(pkg.keg_codes!).join(', ');
                        } catch {
                          return 'N/A';
                        }
                      })()
                    : 'N/A';
                const locLabel =
                  locations.find(loc => loc.locationId === pkg.locationId)?.name ||
                  pkg.locationName ||
                  pkg.locationId ||
                  'N/A';
                const canEdit = !pkg.readOnly && batch.status !== Status.Completed;
                return (
                <div key={pkg.id} className="packaging-card card mb-2">
                  <div className="card-body">
                    <p className="card-text"><strong>Date:</strong> {pkg.date}</p>
                    <p className="card-text"><strong>Package Type:</strong> {pkg.packageType}</p>
                    <p className="card-text"><strong>Quantity:</strong> {pkg.quantity}</p>
                    <p className="card-text"><strong>Volume:</strong> {Number(pkg.volume || 0).toFixed(3)} barrels</p>
                    <p className="card-text"><strong>Location:</strong> {locLabel}</p>
                    <p className="card-text"><strong>Keg Codes:</strong> {kegLabel}</p>
                    <p className="card-text">
                      <strong>Source:</strong> {pkg.source === 'import' ? 'Ekos import (ledger)' : 'Recorded'}
                    </p>
                    {canEdit && (
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-sm" onClick={() => setEditPackaging(pkg)}>
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeletePackaging(pkg)}
                          disabled={pendingDeletions.has(pkg.id.toString())}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : (
          <p>No packaging actions recorded. (Imported packaging appears here from inventory ledger when sourceRef matches this batch.)</p>
        )}
      </div>
      {showVolumePrompt && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          data-testid="batch-volume-shortfall"
          role="dialog"
          aria-modal="true"
          aria-labelledby="batch-volume-shortfall-title"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px', margin: '1rem auto', width: 'calc(100% - 1.5rem)' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="batch-volume-shortfall-title">
                  Adjust brite WIP?
                </h5>
              </div>
              <div className="modal-body">
                <p>{showVolumePrompt.message}</p>
              </div>
              <div className="modal-footer" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="batch-volume-shortfall-yes"
                  onClick={() => handleVolumeAdjustment(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-testid="batch-volume-shortfall-no"
                  onClick={() => handleVolumeAdjustment(false)}
                >
                  No / Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showTrueUpPrompt && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '480px', margin: '0 auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Complete Batch — Production True-up</h5>
            </div>
            <div className="modal-body">
              <p className="mb-2">
                {showTrueUpPrompt.message ||
                  'Confirm production true-up before completing this batch.'}
              </p>
              <ul className="mb-2" style={{ paddingLeft: '1.2rem' }}>
                <li>
                  {`Estimated (into brite): ${showTrueUpPrompt.estimatedVolume.toFixed(3)} ${
                    showTrueUpPrompt.unit || 'bbl'
                  }`}
                </li>
                <li>
                  {`Packaged: ${showTrueUpPrompt.packagedVolume.toFixed(3)} ${
                    showTrueUpPrompt.unit || 'bbl'
                  }`}
                </li>
                {showTrueUpPrompt.destroyedVolume > 0 && (
                  <li>
                    {`Destroyed: ${showTrueUpPrompt.destroyedVolume.toFixed(3)} ${
                      showTrueUpPrompt.unit || 'bbl'
                    }`}
                  </li>
                )}
                {showTrueUpPrompt.trubVolume > 0 && (
                  <li>
                    {`Trub removed from production: ${showTrueUpPrompt.trubVolume.toFixed(3)} ${
                      showTrueUpPrompt.unit || 'bbl'
                    }`}
                  </li>
                )}
                {showTrueUpPrompt.trueUpIncrease > 0 && (
                  <li>
                    {`Production true-up (increase): ${showTrueUpPrompt.trueUpIncrease.toFixed(3)} ${
                      showTrueUpPrompt.unit || 'bbl'
                    }`}
                  </li>
                )}
              </ul>
              <p className="text-muted small mb-0">
                Trub is removed from TTB line 2 (never produced) — not recorded as a line 30
                loss. Leftover Brite WIP is cleared. Mid-quarter unpackaged brite still shows at
                its estimate on line 33 until complete.
                {showTrueUpPrompt.priorPeriodFlag
                  ? ` Prior-period flag: true-up dated ${showTrueUpPrompt.eventDate || 'today'} in the open quarter (closed quarter unchanged).`
                  : ''}
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                data-testid="batch-complete-trueup-confirm"
                onClick={() => handleTrueUpConfirmation(true)}
              >
                Confirm &amp; Complete
              </button>
              <button
                className="btn btn-secondary"
                data-testid="batch-complete-trueup-cancel"
                onClick={() => handleTrueUpConfirmation(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showReopenPrompt && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '440px', margin: '0 auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Reopen Batch</h5>
            </div>
            <div className="modal-body">
              <p className="mb-2">
                Restores any closing loss volume (and Brite WIP) so packaging or a skipped close loss
                can continue. Existing packaging and finished-goods inventory stay unchanged. A
                reason is required for the TTB audit trail.
              </p>
              <label className="form-label" htmlFor="reopen-reason">
                Reason
              </label>
              <textarea
                id="reopen-reason"
                className="form-control"
                rows={3}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Remaining 12 bbl needs to be canned"
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-warning"
                onClick={handleReopenBatch}
                disabled={!reopenReason.trim()}
              >
                Reopen Batch
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowReopenPrompt(false);
                  setReopenReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showKegPrompt && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Reconcile keg assets</h5>
            </div>
            <div className="modal-body">
              <p>
                Packaging into a keg requires scanning or entering exactly {packageQuantity} keg
                serial{packageQuantity === 1 ? '' : 's'} (currently {kegCodes.length}).
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowKegPrompt(false)}>
                Scan Kegs
              </button>
            </div>
          </div>
        </div>
      )}
      {showProductionPrompt && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-content" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <div className="modal-header">
              <h5 className="modal-title">Record Produced Spirits</h5>
            </div>
            <div className="modal-body">
              <label className="form-label">Produced Volume (gallons):</label>
              <input
                type="number"
                value={producedVolume || ''}
                onChange={(e) => setProducedVolume(parseFloat(e.target.value) || 0)}
                className="form-control mb-2"
              />
              <label className="form-label">Produced Proof:</label>
              <input
                type="number"
                value={producedProof || ''}
                onChange={(e) => setProducedProof(parseFloat(e.target.value) || 0)}
                className="form-control"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleProductionConfirmation}>
                Record and Complete
              </button>
              <button className="btn btn-danger" onClick={() => setShowProductionPrompt(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetails;